import { jsPDF } from "jspdf";
import { IInvoiceEntity } from "dto/Invoice/invoiceEntity";
import { IInvoiceRow, IVatRate, InvoiceUnits } from "dto/Invoice/initInvoice";

export interface IInvoicePdfData {
  seller: IInvoiceEntity;
  buyer: IInvoiceEntity;
  invoiceNumber: string;
  invoiceType: number;
  creationDate: Date;
  sellDate?: Date;
  paymentDate: Date;
  paymentType: number;
  rows: IInvoiceRow[];
  vatRates: IVatRate[];
  note?: string;
}

const UNIT_NAMES: Record<number, string> = {
  [InvoiceUnits.Piece]: "szt.",
  [InvoiceUnits.Hour]: "h",
  [InvoiceUnits.Kg]: "kg",
  [InvoiceUnits.Km]: "km",
  [InvoiceUnits.Liter]: "l",
  [InvoiceUnits.Meter]: "m",
  [InvoiceUnits.SquareMeter]: "m²",
  [InvoiceUnits.CubicMeter]: "m³",
  [InvoiceUnits.Ton]: "t",
  [InvoiceUnits.Service]: "usł.",
};

const PAYMENT_NAMES = [
  "przelew bankowy",
  "karta płatnicza",
  "gotówka",
];

const INVOICE_TYPE_NAMES = [
  "FAKTURA VAT",
  "FAKTURA BEZ VAT",
  "FAKTURA ZALICZKOWA",
  "FAKTURA KOŃCOWA",
  "FAKTURA KORYGUJĄCA",
  "DUPLIKAT",
  "NOTA KORYGUJĄCA",
];

// Column definitions: [header, widthMm, textAlign]
type Align = "left" | "center" | "right";
const COLS: [string, number, Align][] = [
  ["Lp.", 8, "center"],
  ["Nazwa towaru / usługi", 54, "left"],
  ["Ilość", 13, "right"],
  ["Jm", 11, "center"],
  ["Cena netto", 19, "right"],
  ["Wartość netto", 21, "right"],
  ["Stawka VAT", 14, "center"],
  ["Kwota VAT", 18, "right"],
  ["Wartość brutto", 24, "right"],
];
// Total: 8+54+13+11+19+21+14+18+24 = 182mm

const fmt = (v: number) =>
  (Number(v) || 0).toLocaleString("pl-PL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (d?: Date) =>
  d ? d.toLocaleDateString("pl-PL") : "—";

// Font cache — loaded once, reused for all subsequent calls
let fontRegularB64: string | null = null;
let fontBoldB64: string | null = null;

async function loadFontB64(url: string): Promise<string> {
  const res = await fetch(url);
  const buf = await res.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = "";
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

export async function generateInvoicePdf(data: IInvoicePdfData): Promise<Blob> {
  if (!fontRegularB64) {
    fontRegularB64 = await loadFontB64("/fonts/LiberationSans-Regular.ttf");
  }
  if (!fontBoldB64) {
    fontBoldB64 = await loadFontB64("/fonts/LiberationSans-Bold.ttf");
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  doc.addFileToVFS("LiberationSans-Regular.ttf", fontRegularB64);
  doc.addFont("LiberationSans-Regular.ttf", "LiberationSans", "normal");
  doc.addFileToVFS("LiberationSans-Bold.ttf", fontBoldB64);
  doc.addFont("LiberationSans-Bold.ttf", "LiberationSans", "bold");

  const L = 14; // left margin
  const R = 196; // right edge (210 - 14)
  const W = 182; // content width
  const ROW_H = 7;
  const HEADER_H = 9;

  let y = 14;

  // ── Title block ───────────────────────────────────────────────────
  const title =
    INVOICE_TYPE_NAMES[data.invoiceType] ?? INVOICE_TYPE_NAMES[0];
  doc.setFont("LiberationSans", "bold");
  doc.setFontSize(16);
  doc.setTextColor(28, 27, 31);
  doc.text(title, L, y + 6);

  if (data.invoiceNumber) {
    doc.setFontSize(9);
    doc.setFont("LiberationSans", "normal");
    doc.setTextColor(80, 80, 80);
    doc.text(`Nr: ${data.invoiceNumber}`, L, y + 11);
  }

  doc.setFontSize(8.5);
  doc.setFont("LiberationSans", "normal");
  doc.setTextColor(60, 60, 60);
  const rightLines = [
    `Data wystawienia: ${fmtDate(data.creationDate)}`,
    data.sellDate ? `Data sprzedaży: ${fmtDate(data.sellDate)}` : null,
    `Termin płatności: ${fmtDate(data.paymentDate)}`,
    `Sposób płatności: ${PAYMENT_NAMES[data.paymentType] ?? ""}`,
  ].filter(Boolean) as string[];
  rightLines.forEach((line, i) => {
    doc.text(line, R, y + 4 + i * 4.5, { align: "right" });
  });

  y += 20;

  // ── Separator ────────────────────────────────────────────────────
  doc.setLineWidth(0.4);
  doc.setDrawColor(197, 186, 218);
  doc.line(L, y, R, y);
  y += 5;

  // ── Parties ──────────────────────────────────────────────────────
  const partyW = 85;
  const buyerX = L + partyW + 12;

  const drawParty = (label: string, entity: IInvoiceEntity, px: number) => {
    doc.setFontSize(7);
    doc.setFont("LiberationSans", "bold");
    doc.setTextColor(140, 130, 160);
    doc.text(label, px, y);

    doc.setFontSize(9);
    doc.setTextColor(28, 27, 31);
    doc.text(entity.Name || "—", px, y + 5);

    doc.setFontSize(8.5);
    doc.setFont("LiberationSans", "normal");
    doc.setTextColor(60, 60, 60);
    let ey = y + 10;
    if (entity.Nip) {
      doc.text(`NIP: ${entity.Nip}`, px, ey);
      ey += 4.5;
    }
    if (entity.Address) {
      const lines = doc.splitTextToSize(entity.Address, partyW);
      doc.text(lines, px, ey);
      ey += lines.length * 4.5;
    }
    if (entity.Email) {
      doc.text(entity.Email, px, ey);
    }
  };

  drawParty("SPRZEDAWCA", data.seller, L);
  drawParty("NABYWCA", data.buyer, buyerX);

  y += 34;

  // ── Separator ────────────────────────────────────────────────────
  doc.setLineWidth(0.4);
  doc.setDrawColor(197, 186, 218);
  doc.line(L, y, R, y);
  y += 5;

  // ── Table header ─────────────────────────────────────────────────
  doc.setFillColor(234, 228, 244);
  doc.rect(L, y, W, HEADER_H, "F");

  doc.setFontSize(7);
  doc.setFont("LiberationSans", "bold");
  doc.setTextColor(70, 60, 90);

  let cx = L;
  for (const [lbl, w, align] of COLS) {
    const tx =
      align === "right"
        ? cx + w - 1.5
        : align === "center"
        ? cx + w / 2
        : cx + 1.5;
    const lines = doc.splitTextToSize(lbl, w - 2);
    lines.forEach((ln: string, li: number) => {
      doc.text(
        ln,
        tx,
        y + 2.5 + li * 3.2,
        { align, baseline: "top" },
      );
    });
    cx += w;
  }

  doc.setLineWidth(0.3);
  doc.setDrawColor(180, 168, 206);
  doc.line(L, y + HEADER_H, R, y + HEADER_H);
  y += HEADER_H;

  // ── Table rows ───────────────────────────────────────────────────
  doc.setFont("LiberationSans", "normal");

  data.rows.forEach((row, i) => {
    const vatRate = data.vatRates.find((r) => r.Id === row.VatRate);
    const vatLabel = vatRate ? `${Math.round(vatRate.Rate * 100)}%` : "";
    const unitName = UNIT_NAMES[row.Unit] ?? "";

    if (i % 2 === 1) {
      doc.setFillColor(248, 244, 254);
      doc.rect(L, y, W, ROW_H, "F");
    }

    const values = [
      String(i + 1),
      row.Description || "",
      fmt(row.Quantity),
      unitName,
      fmt(row.Netto),
      fmt(row.TotalNetto),
      vatLabel,
      fmt(row.Vat),
      fmt(row.Total),
    ];

    doc.setFontSize(8);
    doc.setTextColor(28, 27, 31);
    cx = L;
    values.forEach((val, ci) => {
      const [, w, align] = COLS[ci];
      let text = val;
      if (ci === 1) {
        const clipped = doc.splitTextToSize(val, w - 2);
        text = clipped[0] + (clipped.length > 1 ? "..." : "");
      }
      const tx =
        align === "right"
          ? cx + w - 1.5
          : align === "center"
          ? cx + w / 2
          : cx + 1.5;
      doc.text(text, tx, y + ROW_H / 2, { align, baseline: "middle" });
      cx += w;
    });

    doc.setLineWidth(0.1);
    doc.setDrawColor(220, 212, 235);
    doc.line(L, y + ROW_H, R, y + ROW_H);
    y += ROW_H;
  });

  // ── Razem row ────────────────────────────────────────────────────
  y += 1;
  doc.setLineWidth(0.4);
  doc.setDrawColor(180, 168, 206);
  doc.line(L, y, R, y);
  y += 1;

  const totals = data.rows.reduce(
    (acc, r) => {
      acc.net += r.TotalNetto || 0;
      acc.vat += r.Vat || 0;
      acc.gross += r.Total || 0;
      return acc;
    },
    { net: 0, vat: 0, gross: 0 },
  );

  // Column x-positions (cumulative offsets from L):
  // Lp(8) | Nazwa(54) | Ilość(13) | Jm(11) | Cena netto(19) | Wartość netto(21) | Stawka(14) | Kwota VAT(18) | Wartość brutto(24)
  const NET_X = L + 8 + 54 + 13 + 11;       // start of Cena netto  = L+86
  const WARTN_X = NET_X + 19;                // start of Wartość netto = L+105
  const STAWKA_X = WARTN_X + 21;             // = L+126
  const KWOTAVAT_X = STAWKA_X + 14;          // = L+140
  const WARTB_X = KWOTAVAT_X + 18;           // = L+158

  doc.setFontSize(8.5);
  doc.setFont("LiberationSans", "bold");
  doc.setTextColor(28, 27, 31);
  // "Razem:" right-aligned in Cena netto column — keeps it clear of the Wartość netto value
  doc.text("Razem:", NET_X + 19 - 1.5, y + ROW_H / 2, { align: "right", baseline: "middle" });
  doc.text(fmt(totals.net), WARTN_X + 21 - 1.5, y + ROW_H / 2, {
    align: "right",
    baseline: "middle",
  });
  doc.text(fmt(totals.vat), KWOTAVAT_X + 18 - 1.5, y + ROW_H / 2, {
    align: "right",
    baseline: "middle",
  });
  doc.text(fmt(totals.gross), WARTB_X + 24 - 1.5, y + ROW_H / 2, {
    align: "right",
    baseline: "middle",
  });

  y += ROW_H + 1;

  // ── VAT breakdown ────────────────────────────────────────────────
  const vatSummary = data.vatRates
    .filter((r) => r.Rate > 0)
    .map((rate) => ({
      rate,
      vat: data.rows
        .filter((r) => r.VatRate === rate.Id)
        .reduce((s, r) => s + (r.Vat || 0), 0),
    }))
    .filter((s) => s.vat !== 0);

  if (vatSummary.length > 0) {
    doc.setFontSize(7.5);
    doc.setFont("LiberationSans", "normal");
    doc.setTextColor(100, 90, 120);
    doc.text("w tym:", NET_X + 19 - 1.5, y + ROW_H / 2, { align: "right", baseline: "middle" });

    vatSummary.forEach(({ rate, vat }) => {
      const rateLabel = `${Math.round(rate.Rate * 100)}%`;
      doc.text(rateLabel, STAWKA_X + 7, y + ROW_H / 2, {
        align: "center",
        baseline: "middle",
      });
      doc.text(fmt(vat), KWOTAVAT_X + 18 - 1.5, y + ROW_H / 2, {
        align: "right",
        baseline: "middle",
      });
      y += ROW_H;
    });
  }

  // ── Notes ────────────────────────────────────────────────────────
  if (data.note?.trim()) {
    y += 4;
    doc.setLineWidth(0.2);
    doc.setDrawColor(210, 205, 225);
    doc.line(L, y, R, y);
    y += 5;
    doc.setFontSize(7);
    doc.setFont("LiberationSans", "bold");
    doc.setTextColor(120, 110, 140);
    doc.text("Uwagi:", L, y);
    y += 4;
    doc.setFontSize(8.5);
    doc.setFont("LiberationSans", "normal");
    doc.setTextColor(40, 40, 40);
    const noteLines = doc.splitTextToSize(data.note, W);
    doc.text(noteLines, L, y);
  }

  return doc.output("blob");
}
